import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { templatesApi, blocksApi, sectionsApi } from '../services/api';
import { Template, Block, Section } from '../types';
import DocumentDesigner, { DesignerDocument } from '../components/DocumentDesigner';
import {
  Save,
  ArrowLeft,
  GripVertical,
  Plus,
  Trash2,
  AlertCircle,
  Check,
  Layers
} from 'lucide-react';

// Sortable Block Item
function SortableBlockItem({
  block,
  onRemove
}: {
  block: Block;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-lg border border-gray-200 p-3 flex items-center"
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 mr-3 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-900 truncate">{block.name}</h4>
        <p className="text-xs text-gray-500">
          {block.section?.name || 'Ohne Abteil'} · {block.variables?.length || 0} Variable(n)
        </p>
      </div>

      <button
        onClick={onRemove}
        className="p-1.5 text-gray-400 hover:text-red-600 rounded ml-2"
        title="Entfernen"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function TemplateEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [template, setTemplate] = useState<Template | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true
  });
  const [selectedBlocks, setSelectedBlocks] = useState<Block[]>([]);
  const [availableBlocks, setAvailableBlocks] = useState<Block[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [selectedSection, setSelectedSection] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [document, setDocument] = useState<DesignerDocument>({ schemaVersion: 1, nodes: [] });
  const [revision, setRevision] = useState(1);
  const [saveStatus, setSaveStatus] = useState<'idle'|'changed'|'saving'|'saved'|'offline'|'conflict'>('idle');
  const [recovery, setRecovery] = useState<any>(null);
  const [conflictServer, setConflictServer] = useState<Template|null>(null);
  const [versions,setVersions]=useState<any[]>([]);
  const loadedRef = useRef(false);
  const revisionRef = useRef(1);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    try {
      // Blocks und Sections laden
      const [blocksRes, sectionsRes] = await Promise.all([
        blocksApi.getAll(),
        sectionsApi.getAll()
      ]);
      
      setAvailableBlocks(blocksRes.blocks.filter((b: Block) => b.isActive));
      setSections(sectionsRes.sections);

      // Wenn bearbeiten, Template laden
      if (!isNew && id) {
        const templateRes = await templatesApi.getById(id);
        setTemplate(templateRes.template);
        setRevision(templateRes.template.revision || 1);
        revisionRef.current = templateRes.template.revision || 1;
        if (templateRes.template.documentJson) {
          try { setDocument(JSON.parse(templateRes.template.documentJson)); } catch { setError('Das Dokumentformat der Vorlage ist beschädigt'); }
        }
        templatesApi.getVersions(id).then(result=>setVersions(result.versions)).catch(()=>setVersions([]));
        setFormData({
          name: templateRes.template.name,
          description: templateRes.template.description || '',
          isActive: templateRes.template.isActive
        });
        
        // Template-Blöcke als ausgewählte Blöcke setzen
        if (templateRes.template.blocks) {
          const templateBlocks = templateRes.template.blocks
            .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
            .map((tb: any) => tb.block)
            .filter(Boolean);
          setSelectedBlocks(templateBlocks);
        }
      }
      const local = localStorage.getItem(`template-draft:${id || 'new'}`);
      if (local) { try { const parsed=JSON.parse(local); if(parsed.savedAt && Date.now()-parsed.savedAt<7*86400000)setRecovery(parsed); } catch {} }
    } catch (err) {
      setError('Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
      setTimeout(() => { loadedRef.current = true; }, 0);
    }
  };

  useEffect(() => {
    if (!loadedRef.current) return;
    setSaveStatus('changed');
    const snapshot = { formData, document, blockIds: selectedBlocks.map(block => block.id), savedAt: Date.now() };
    localStorage.setItem(`template-draft:${id || 'new'}`, JSON.stringify(snapshot));
    if (isNew || !id || !formData.name.trim()) return;
    const timer = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        const result = await templatesApi.update(id, { ...formData, document, revision: revisionRef.current, blocks: selectedBlocks.map((block,index) => ({ blockId:block.id, sortOrder:index })), changeNote:'Automatische Speicherung' });
        const next = result.template.revision || revisionRef.current + 1; revisionRef.current = next; setRevision(next); setSaveStatus('saved'); localStorage.removeItem(`template-draft:${id}`);
      } catch (error:any) { if(error.response?.status===409){setSaveStatus('conflict');try{setConflictServer((await templatesApi.getById(id)).template)}catch{}}else setSaveStatus('offline'); }
    }, 2500);
    return () => clearTimeout(timer);
  }, [document, formData, selectedBlocks, id, isNew]);

  const handleAddBlock = (block: Block) => {
    if (!selectedBlocks.find(b => b.id === block.id)) {
      setSelectedBlocks([...selectedBlocks, block]);
    }
  };

  const handleRemoveBlock = (blockId: string) => {
    setSelectedBlocks(selectedBlocks.filter(b => b.id !== blockId));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSelectedBlocks((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Bitte geben Sie einen Namen ein');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const data = {
        ...formData,
        document,
        revision: isNew ? undefined : revision,
        blocks: selectedBlocks.map((block, index) => ({
          blockId: block.id,
          sortOrder: index
        }))
      };

      if (isNew) {
        const result = await templatesApi.create(data);
        setSuccessMessage('Vorlage erfolgreich erstellt');
        setTimeout(() => navigate(`/templates/${result.template.id}`), 1500);
      } else {
        const result = await templatesApi.update(id!, data);
        const next=result.template.revision || revision + 1; setRevision(next); revisionRef.current=next;
        localStorage.removeItem(`template-draft:${id}`);
        setSuccessMessage('Vorlage erfolgreich gespeichert');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err: any) {
      setError(err.response?.status === 409 ? 'Speicherkonflikt: Die Vorlage wurde an anderer Stelle geändert. Bitte laden Sie die Seite neu.' : err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredBlocks = selectedSection
    ? availableBlocks.filter(b => b.sectionId === selectedSection || (!b.sectionId && selectedSection === 'none'))
    : availableBlocks;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/templates"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück zu Vorlagen
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            {isNew ? 'Neue Vorlage erstellen' : 'Vorlage bearbeiten'}
          </h1>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Wird gespeichert...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Speichern
              </>
            )}
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="mb-3 text-xs text-gray-500" role="status">Speicherstatus: {{idle:'bereit',changed:'ungespeicherte Änderungen',saving:'wird automatisch gespeichert …',saved:'automatisch gespeichert',offline:'offline – lokal gesichert',conflict:'Konflikt – Serverfassung wurde geändert'}[saveStatus]}</div>
      {recovery && <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex justify-between gap-4"><span>Eine lokale Fassung vom {new Date(recovery.savedAt).toLocaleString('de-AT')} wurde gefunden.</span><span className="whitespace-nowrap"><button className="underline mr-4" onClick={()=>{setFormData(recovery.formData);setDocument(recovery.document);setSelectedBlocks(availableBlocks.filter(block=>recovery.blockIds?.includes(block.id)));setRecovery(null)}}>Wiederherstellen</button><button className="underline" onClick={()=>{localStorage.removeItem(`template-draft:${id||'new'}`);setRecovery(null)}}>Verwerfen</button></span></div>}
      {conflictServer&&<div className="mb-4 border border-red-300 bg-red-50 rounded-lg p-4" role="alertdialog" aria-label="Speicherkonflikt"><h3 className="font-semibold text-red-800">Paralleländerung erkannt</h3><div className="grid grid-cols-2 gap-3 my-3 text-xs"><div className="bg-white p-2 rounded"><strong>Ihre lokale Fassung</strong><pre className="overflow-auto max-h-32 whitespace-pre-wrap">{JSON.stringify(document,null,2)}</pre></div><div className="bg-white p-2 rounded"><strong>Serverfassung (Revision {conflictServer.revision})</strong><pre className="overflow-auto max-h-32 whitespace-pre-wrap">{conflictServer.documentJson||'Leeres Dokument'}</pre></div></div><button className="border rounded px-3 py-2 mr-2" onClick={()=>{if(conflictServer.documentJson)setDocument(JSON.parse(conflictServer.documentJson));revisionRef.current=conflictServer.revision||1;setRevision(revisionRef.current);setConflictServer(null);setSaveStatus('idle')}}>Serverfassung laden</button><button className="border rounded px-3 py-2" onClick={()=>{revisionRef.current=conflictServer.revision||1;setRevision(revisionRef.current);setConflictServer(null);setDocument(JSON.parse(JSON.stringify(document)));setSaveStatus('changed')}}>Meine Fassung als nächste Revision speichern</button></div>}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700">
          <Check className="h-5 w-5 mr-2" />
          {successMessage}
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2">Dokument gestalten</h2>
        <p className="text-sm text-gray-500 mb-4">Text, Tabellen mit Formeln, QR-Codes und Signaturfelder direkt in der Vorlage anordnen.</p>
        <DocumentDesigner
          value={document}
          onChange={setDocument}
          blocks={availableBlocks}
          variables={Array.from(new Set(availableBlocks.flatMap(block => (block.variables || []).map(variable => variable.name))))}
        />
      </div>
      {!isNew&&<details className="mb-6 bg-white border rounded-lg p-4"><summary className="font-semibold cursor-pointer">Versionshistorie ({versions.length})</summary><div className="mt-3 divide-y">{versions.map(version=><div key={version.id} className="py-2 flex justify-between text-sm"><span>Revision {version.revision} · {version.changeNote||'Änderung'}</span><span>{new Date(version.createdAt).toLocaleString('de-AT')}</span></div>)}</div></details>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Template Info */}
        <div className="lg:col-span-1 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Vorlagen-Details</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="z.B. Standard Mietvertrag"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beschreibung
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Optionale Beschreibung der Vorlage"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  Vorlage aktiv (für neue Verträge verfügbar)
                </label>
              </div>
            </div>
          </div>

          {/* Available Blocks */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Verfügbare Bauteile</h2>
            
            <div className="mb-4">
              <select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
              >
                <option value="">Alle Abteile</option>
                <option value="none">Ohne Abteil</option>
                {sections.map((section) => (
                  <option key={section.id} value={section.id}>{section.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {filteredBlocks.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Keine Bauteile verfügbar
                </p>
              ) : (
                filteredBlocks.map((block) => {
                  const isSelected = selectedBlocks.some(b => b.id === block.id);
                  return (
                    <button
                      key={block.id}
                      onClick={() => !isSelected && handleAddBlock(block)}
                      disabled={isSelected}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        isSelected
                          ? 'bg-gray-50 border-gray-200 opacity-50 cursor-not-allowed'
                          : 'bg-white border-gray-200 hover:border-primary-300 hover:bg-primary-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900 text-sm">{block.name}</h4>
                          <p className="text-xs text-gray-500">
                            {block.section?.name || 'Ohne Abteil'}
                          </p>
                        </div>
                        {isSelected ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Plus className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Right: Selected Blocks */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                Ausgewählte Bauteile
              </h2>
              <span className="text-sm text-gray-500">
                {selectedBlocks.length} Bauteil(e)
              </span>
            </div>

            {selectedBlocks.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                <Layers className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  Klicken Sie links auf Bauteile, um sie hinzuzufügen
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Die Reihenfolge kann per Drag & Drop angepasst werden
                </p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={selectedBlocks.map(b => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {selectedBlocks.map((block) => (
                      <SortableBlockItem
                        key={block.id}
                        block={block}
                        onRemove={() => handleRemoveBlock(block.id)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {/* Preview Info */}
            {selectedBlocks.length > 0 && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="text-sm font-medium text-blue-800 mb-2">Vorschau der Struktur:</h3>
                <div className="space-y-1">
                  {selectedBlocks.map((block, index) => (
                    <div key={block.id} className="text-sm text-blue-700">
                      {block.sectionId ? `§${index + 1}` : '•'} {block.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
